import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Link,
  ListItem,
  SimpleGrid,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import Section from "./Section";

const Contact: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Section title={t("contact.title")} maxW="1400px">
      <VStack spacing={7} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          <Flex
            align="flex-start"
            gap={3}
            p={5}
            borderRadius="xl"
            bg="whiteAlpha.800"
            border="1px solid"
            borderColor="blackAlpha.100"
          >
            <EmailIcon mt={1} color="primary.500" />
            <Box>
              <Text color="gray.500" fontSize="sm" mb={1}>
                {t("contact.email")}
              </Text>
              <Link
                href="mailto:marketing@deepsoulai.com"
                color="gray.800"
                fontWeight="700"
              >
                marketing@deepsoulai.com
              </Link>
            </Box>
          </Flex>

          <Flex
            align="flex-start"
            gap={3}
            p={5}
            borderRadius="xl"
            bg="whiteAlpha.800"
            border="1px solid"
            borderColor="blackAlpha.100"
          >
            <PhoneIcon mt={1} color="primary.500" />
            <Box>
              <Text color="gray.500" fontSize="sm" mb={1}>
                {t("contact.phone")}
              </Text>
              <Link href={`tel:${t("contact.phoneValue").replace(/\s/g, "")}`} fontWeight="700">
                {t("contact.phoneValue")}
              </Link>
            </Box>
          </Flex>

          <Box
            p={5}
            borderRadius="xl"
            bg="whiteAlpha.800"
            border="1px solid"
            borderColor="blackAlpha.100"
          >
            <Text color="gray.500" fontSize="sm" mb={1}>
              {t("contact.officeAddress")}
            </Text>
            <Text color="gray.800" fontWeight="700" lineHeight="1.7">
              {t("contact.officeAddressDetail")}
            </Text>
          </Box>
        </SimpleGrid>

        <Accordion allowToggle>
          <AccordionItem
            border="1px solid"
            borderColor="blackAlpha.100"
            borderRadius="xl"
            overflow="hidden"
            bg="whiteAlpha.800"
          >
            <h3>
              <AccordionButton
                px={{ base: 5, md: 6 }}
                py={5}
                _hover={{ bg: "blackAlpha.50" }}
                _expanded={{ bg: "rgba(255, 63, 79, 0.04)" }}
              >
                <Box flex="1" textAlign="left">
                  <Heading as="span" display="block" fontSize="md">
                    {t("contact.emailTemplate")}
                  </Heading>
                  <Text mt={1} color="gray.600" fontSize="sm">
                    {t("contact.emailTemplateDescription")}
                  </Text>
                </Box>
                <AccordionIcon ml={4} />
              </AccordionButton>
            </h3>
            <AccordionPanel
              px={{ base: 5, md: 6 }}
              py={5}
              borderTop="1px solid"
              borderColor="blackAlpha.100"
            >
              <Box
                bg="rgba(255, 255, 255, 0.72)"
                border="1px solid"
                borderColor="blackAlpha.100"
                p={{ base: 4, md: 5 }}
                borderRadius="lg"
              >
                <UnorderedList spacing={3}>
                  <ListItem>
                    <Text as="span" fontWeight="700">
                      {t("contact.subject")}:
                    </Text>{" "}
                    {t("contact.subjectPlaceholder")}
                  </ListItem>
                  <ListItem>
                    <Text fontWeight="700">
                      {t("contact.templateStructure")}:
                    </Text>
                    <VStack align="start" pl={2} mt={2} spacing={2}>
                      <Text>1. {t("contact.yourNameCompany")}</Text>
                      <Text>2. {t("contact.purposeContent")}</Text>
                      <Text>3. {t("contact.yourContactInfo")}</Text>
                    </VStack>
                  </ListItem>
                </UnorderedList>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        <Flex justify={{ base: "stretch", md: "flex-end" }}>
          <Link
            href="mailto:marketing@deepsoulai.com"
            display="inline-flex"
            justifyContent="center"
            alignItems="center"
            minH="48px"
            px={7}
            borderRadius="full"
            bg="primary.500"
            color="white"
            fontWeight="700"
            textDecoration="none"
            _hover={{ bg: "primary.600", textDecoration: "none" }}
            _focusVisible={{
              outline: "3px solid",
              outlineColor: "primary.300",
              outlineOffset: "3px",
            }}
          >
            {t("contact.emailCta")}
          </Link>
        </Flex>
      </VStack>
    </Section>
  );
};

export default Contact;
